import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

/**
 * Bump when the cached payload shape changes; invalidates every stored entry.
 * @type {string}
 */
const CACHE_VERSION = 'v1';
const CACHE_PREFIX = 'pr-cache:';
/** Max entries kept in sessionStorage (LRU-evicted). Payloads are large, quota is ~5MB. */
const CACHE_MAX_ENTRIES = 6;
/** Never store an entry bigger than this; it would evict everything else for one product. */
const CACHE_MAX_BYTES = 400_000;
/** Paint from cache only if it is fresher than this. Revalidation always runs. */
const CACHE_MAX_AGE_MS = 15 * 60 * 1000;

class ProductRecommendations extends Component {
  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations({ ignoreCache: true });
      break;
    }
  });

  /**
   * The cached recommendations for this instance (per-page, in memory)
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** Guards against the prefetch and the scroll trigger both firing. @type {boolean} */
  #started = false;

  /**
   * The observer for the product recommendations. Retained as the early path: if the shopper
   * scrolls down before the page has finished loading, fetch straight away rather than waiting.
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#start();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();
    this.#mutationObserver.observe(this, { attributes: true });
    this.#intersectionObserver.observe(this);

    // These sections are below the fold, so the old scroll-only gate meant the request did not
    // even start until the shopper was already staring at a skeleton. Prefetch instead, on the
    // first idle slot after DOMContentLoaded. Measured on this store: DCL ~2.7-3.3s, first idle
    // ~3.0-3.8s, `load` ~6.4-10.2s. Firing at DCL itself puts a ~400KB section response in
    // contention with the page's own images and scripts; waiting for `load` is far too late.
    // Whichever comes first, scroll or idle, wins — #start() is idempotent.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#schedulePrefetch(), { once: true });
    } else {
      this.#schedulePrefetch();
    }
  }

  #schedulePrefetch() {
    const run = () => this.#start();
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 200);
    }
  }

  #start() {
    if (this.#started) return;
    this.#started = true;
    this.#loadRecommendations();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mutationObserver.disconnect();
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * The sessionStorage key for this instance
   * @returns {string | null}
   */
  get #cacheKey() {
    const { productId, sectionId, intent } = this.dataset;
    if (!productId || !sectionId) return null;
    const handle = window.location.pathname.split('/products/')[1]?.split(/[?#]/)[0] ?? '';
    const themeId = window.Shopify?.theme?.id ?? '';
    return `${CACHE_PREFIX}${CACHE_VERSION}:${themeId}:${handle}:${productId}:${sectionId}:${intent}`;
  }

  /**
   * Load the product recommendations
   * @param {Object} [options]
   * @param {boolean} [options.ignoreCache] - Skip the stored copy (used when attributes change)
   */
  #loadRecommendations({ ignoreCache = false } = {}) {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    // Paint instantly from the previous visit, then revalidate in the background so a stale
    // list never survives longer than one paint.
    let painted = false;
    if (!ignoreCache) {
      const stored = this.#readCache();
      if (stored) {
        painted = this.#render(sectionId, stored);
      }
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (painted) return;
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.#writeCache(sectionId, result.data);
          this.#render(sectionId, result.data);
        } else if (!painted) {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        // A background refresh that fails must not blank out what is already on screen.
        if (painted || e?.name === 'AbortError') return;
        this.#handleError(e);
      });
  }

  /**
   * Morph a section response into the page
   * @param {string} sectionId
   * @param {string} html
   * @returns {boolean} whether the morph succeeded
   */
  #render(sectionId, html) {
    // Flag before morphing, as the original did: the morph can replace this element with the
    // one from the response, and the flag is what stops the fresh element re-fetching.
    const previous = this.dataset.recommendationsPerformed;
    this.dataset.recommendationsPerformed = 'true';
    try {
      morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
      return true;
    } catch {
      this.dataset.recommendationsPerformed = previous ?? 'false';
      return false;
    }
  }

  /**
   * Reduces a full section response to just the part that is actually morphed:
   * the hydration-keyed element plus the section stylesheet. The pairs instance lives inside
   * the main product section, so its raw response carries the entire PDP — storing that
   * verbatim would exhaust the sessionStorage quota after a couple of products.
   * @param {string} sectionId
   * @param {string} html
   * @returns {string | null}
   */
  #compact(sectionId, html) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.getElementById(`shopify-section-${sectionId}`);
      if (!section) return null;
      const hydrated = section.querySelector('[data-hydration-key]');
      if (!hydrated) return null;

      const shell = doc.createElement('div');
      shell.id = `shopify-section-${sectionId}`;
      shell.className = section.className;
      shell.appendChild(hydrated.cloneNode(true));
      for (const style of section.querySelectorAll('style[data-section-stylesheet]')) {
        shell.appendChild(style.cloneNode(true));
      }
      return shell.outerHTML;
    } catch {
      return null;
    }
  }

  /**
   * @returns {string | null}
   */
  #readCache() {
    const key = this.#cacheKey;
    if (!key) return null;
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const { at, html } = JSON.parse(raw);
      if (!html || Date.now() - at > CACHE_MAX_AGE_MS) return null;
      // Refresh recency for the LRU sweep.
      sessionStorage.setItem(key, JSON.stringify({ at, html, used: Date.now() }));
      return html;
    } catch {
      return null;
    }
  }

  /**
   * @param {string} sectionId
   * @param {string} html
   */
  #writeCache(sectionId, html) {
    const key = this.#cacheKey;
    if (!key) return;
    const compact = this.#compact(sectionId, html);
    if (!compact || compact.length > CACHE_MAX_BYTES) return;

    const payload = JSON.stringify({ at: Date.now(), used: Date.now(), html: compact });
    try {
      sessionStorage.setItem(key, payload);
    } catch {
      // Quota hit: drop our own oldest entries and try once more.
      this.#evict(CACHE_MAX_ENTRIES / 2);
      try {
        sessionStorage.setItem(key, payload);
      } catch {
        /* caching is an optimisation; give up quietly */
      }
    }
    this.#evict(CACHE_MAX_ENTRIES);
  }

  /**
   * Keep at most `max` of our entries, dropping least-recently-used first.
   * @param {number} max
   */
  #evict(max) {
    try {
      const entries = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (!k?.startsWith(CACHE_PREFIX)) continue;
        let used = 0;
        try {
          used = JSON.parse(sessionStorage.getItem(k) ?? '{}').used ?? 0;
        } catch {
          /* malformed entry sorts oldest and gets dropped */
        }
        entries.push({ k, used });
      }
      if (entries.length <= max) return;
      entries.sort((a, b) => a.used - b.used);
      for (const { k } of entries.slice(0, entries.length - max)) sessionStorage.removeItem(k);
    } catch {
      /* storage unavailable */
    }
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
